import {
    interpretStrapiError,
    isStrapiErrorClaimed,
    normalizeStrapiError,
    strapiAutoLogoutPolicy,
    strapiErrorPolicy,
    type StrapiErrorSurface,
} from '../utils/strapi/errors'

// Страница входа — единственная поверхность 'login': там ошибка уходит в форму
const LOGIN_ROUTE = '/login'

// Публичное окно ошибки подписанной ссылки (issue #22, приёмка): заказчик здесь
// аноним. 401 от boot-фетча пользователя модулем strapi (протухший cookie) — не
// «протухшая сессия сотрудника»: ни авто-разлогина с уходом на /login, ни тоста
const LINK_ERROR_ROUTE = '/link-expired'

const surfaceFor = (path: string): StrapiErrorSurface => (path === LOGIN_ROUTE ? 'login' : 'global')

export default defineNuxtPlugin((nuxt) => {
    const errorState = useState<Strapi5Error | null>('error', () => (null))

    // Nuxt-контекст есть только в setup плагина: инстанс toast'а, роутер и
    // logout захватываем заранее — решение принимается отложенно, уже вне
    // контекста хука strapi:error
    const toast = useToast()
    const router = useRouter()
    const { logout } = useStrapiAuth()

    // Старт SPA (ssr: false): плагин модуля strapi ждёт fetchUser ещё ДО
    // initial-навигации роутера — router.currentRoute в этот момент
    // START_LOCATION ('/', без matched-записей), и реальный адрес браузера
    // есть только в history. Позже authoritative — resolved route.
    const currentPath = (): string => {
        const route = router.currentRoute.value
        if (!route.matched.length && import.meta.client) return window.location.pathname
        return route.path
    }

    // Guard: страница бьёт несколькими параллельными запросами с одним
    // протухшим токеном — разлогин и редирект делаем ровно один раз на серию
    let loggingOut = false

    const autoLogout = (): void => {
        if (loggingOut || currentPath() === LOGIN_ROUTE) return
        loggingOut = true
        // logout() очищает cookie-токен (useStrapiToken) и пользователя
        // (useState); pinia persist у auth-данных не используется
        logout()
        // replace: протухшая страница не остаётся в истории браузера
        router.replace({ path: LOGIN_ROUTE })
            .finally(() => { loggingOut = false })
    }

    // eslint-disable-next-line
    nuxt.hook('strapi:error' as any, ((raw: unknown) => {
        // Единая интерпретация + политика показа (issue #11)
        const interpreted = interpretStrapiError(raw)
        const path = currentPath()

        // Аноним на публичном окне ошибки подписанной ссылки: 401 тихо (issue #22,
        // приёмка) — заказчик остаётся на странице ошибки, не уходит на /login
        if (interpreted.status === 401 && path === LINK_ERROR_ROUTE) return

        // 401 = сессии нет (токен протух/невалиден/отсутствует): разлогин и
        // редирект на /login вместо тоста — сам редирект и есть сигнал
        if (strapiAutoLogoutPolicy(interpreted.status, surfaceFor(path)) === 'logout') {
            autoLogout()
            return
        }

        if (strapiErrorPolicy(interpreted, 'global') === 'fullscreen') {
            return showError({
                statusCode: interpreted.status,
                statusMessage: interpreted.message,
            })
        }

        if (import.meta.server) {
            errorState.value = { data: null, error: normalizeStrapiError(raw) }
            return
        }

        // Поверхности, взявшие показ на себя (формы — field errors, bulk-панель —
        // сводный тост), заявляют ошибку через claimStrapiError в своём catch.
        // Хук strapi:error срабатывает раньше этих catch'ей, поэтому показ
        // откладывается: catch'и исполняются в микротасках до этого callback'а.
        setTimeout(() => {
            if (isStrapiErrorClaimed(raw)) return
            useToastError({ data: null, error: normalizeStrapiError(raw) }, 'error', toast)
        }, 0)
    }))
})
